import React, { useState, useEffect } from 'react';
import { testimonialsData } from '../data/Testimonials';

const TestimonialsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonialsPerView, setTestimonialsPerView] = useState(3);

  // Calculate average rating
  const averageRating = (
    testimonialsData.reduce((sum, testimonial) => sum + testimonial.rating, 0) /
    testimonialsData.length
  ).toFixed(2);

  // Handle responsive testimonials per view
  useEffect(() => {
    const handleResize = () => {
      let newPerView;
      if (window.innerWidth >= 1024) {
        newPerView = 3; // Desktop: 3 columns
      } else if (window.innerWidth >= 768) {
        newPerView = 2; // Tablet: 2 columns
      } else {
        newPerView = 3; // Mobile: 3 rows (single column)
      }
      setTestimonialsPerView(newPerView);
      // Reset to first slide when changing view count to prevent empty slides
      setCurrentIndex(0);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalGroups = Math.ceil(testimonialsData.length / testimonialsPerView);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalGroups);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalGroups) % totalGroups);
  };

  const getCurrentTestimonials = () => {
    const startIndex = currentIndex * testimonialsPerView;
    return testimonialsData.slice(startIndex, startIndex + testimonialsPerView);
  };

  const renderFilledStars = (rating) => {
    return Array.from({ length: rating }, (_, index) => (
      <svg
        key={index}
        className="w-4 h-4 text-yellow-400 fill-current"
        viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const handleCardClick = () => {
    // Navigate to Google Reviews for Oak and Stone Remodel
    window.open(
      'https://www.google.com/search?q=Oak+and+Stone+Remodel+reviews',
      '_blank'
    );
  };

  return (
    <div className="overflow-hidden relative px-4 py-12 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 md:py-16">
      {/* Elegant Blue Background Pattern */}
      <div className="absolute inset-0">
        {/* Diamond Pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none">
            <defs>
              <pattern
                id="diamonds"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse">
                <path
                  d="M10 0L20 10L10 20L0 10Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.3"
                />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#diamonds)" />
          </svg>
        </div>

        {/* Blue Gradient Orbs */}
        <div className="absolute top-5 left-5 w-40 h-40 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-float"></div>
        <div
          className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-indigo-300 to-purple-400 rounded-full opacity-20 mix-blend-multiply filter blur-3xl animate-float"
          style={{ animationDelay: '2s' }}></div>
        <div
          className="absolute bottom-5 left-1/3 w-36 h-36 bg-gradient-to-br from-purple-300 to-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-float"
          style={{ animationDelay: '4s' }}></div>
        <div
          className="absolute bottom-10 right-1/4 w-28 h-28 bg-gradient-to-br from-cyan-300 to-blue-400 rounded-full opacity-25 mix-blend-multiply filter blur-3xl animate-float"
          style={{ animationDelay: '1s' }}></div>

        {/* Elegant Curves */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-transparent via-blue-100 to-transparent opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-transparent via-indigo-100 to-transparent opacity-30"></div>

        {/* Subtle Radial Gradients */}
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-gradient-to-r from-blue-200 to-indigo-300 rounded-full opacity-5 mix-blend-multiply filter blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
      </div>

      <div className="relative mx-auto max-w-7xl">
        {/* Header Section */}
        <div className="mb-8 text-center md:mb-12">
          <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
            What Our Clients Say
          </h2>
          <div className="flex justify-center items-center mb-2 space-x-2">
            <div className="flex">{renderFilledStars(5)}</div>
            <span className="text-lg font-semibold text-gray-700">
              {averageRating} out of 5
            </span>
          </div>
          <p className="mb-2 text-lg text-gray-600">
            Based on {testimonialsData.length} customer reviews
          </p>
          <p className="text-sm italic text-gray-500">
            Click any review to see more on Google
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="hidden absolute -left-4 top-1/2 z-10 p-4 text-gray-800 bg-white rounded-full border border-gray-200 shadow-lg transition-all duration-200 transform -translate-y-1/2 md:block hover:bg-gray-50 hover:shadow-xl hover:scale-110"
            aria-label="Previous testimonials">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            className="hidden absolute -right-4 top-1/2 z-10 p-4 text-gray-800 bg-white rounded-full border border-gray-200 shadow-lg transition-all duration-200 transform -translate-y-1/2 md:block hover:bg-gray-50 hover:shadow-xl hover:scale-110"
            aria-label="Next testimonials">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 gap-4 px-4 md:grid-cols-2 lg:grid-cols-3 md:gap-6 md:px-12">
            {getCurrentTestimonials().map((testimonial, index) => (
              <div
                key={`${currentIndex}-${index}`}
                onClick={handleCardClick}
                className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-lg transition-all duration-300 transform cursor-pointer hover:shadow-xl hover:-translate-y-1 group">
                <div className={`h-2 ${testimonial.color}`}></div>
                <div className="p-6">
                  {/* Rating */}
                  <div className="flex mb-4">
                    {renderFilledStars(testimonial.rating)}
                  </div>

                  {/* Testimonial Text */}
                  <blockquote className="mb-4 text-sm italic leading-relaxed text-gray-700">
                    "
                    {testimonial.text.length > 200
                      ? `${testimonial.text.substring(0, 200)}...`
                      : testimonial.text}
                    "
                  </blockquote>

                  {/* Author */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div
                        className={`w-10 h-10 rounded-full ${testimonial.color} flex items-center justify-center text-white font-semibold text-sm`}>
                        {testimonial.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-3">
                        <p className="font-semibold text-gray-900">
                          {testimonial.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          Verified Customer
                        </p>
                      </div>
                    </div>
                    {/* Click indicator */}
                    <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Dots */}
        <div className="flex justify-center mt-4 space-x-1 md:mt-8 md:space-x-2">
          {Array.from({ length: totalGroups }, (_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`pagination-dot w-3 h-3 md:w-3 md:h-3 rounded-full transition-all duration-200 ${
                index === currentIndex
                  ? 'bg-primary md:scale-125'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TestimonialsCarousel;
